import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DataPanel, DataPanelInlineItem } from '@/ui';
import { DateFormatter } from '@/features/shared/DateFormatter';
import type { ElsEmergencyContact, ElsRecord } from '../api/elsApi';

export type ElsDataPanelProps = {
  elsRecord: ElsRecord;
};

function hasText(value: string | null | undefined): value is string {
  return value != null && value.trim() !== '';
}

function hasEmergencyInfo(elsRecord: ElsRecord): boolean {
  const emergencyInfo = elsRecord.emergencyInfo;
  if (emergencyInfo == null) {
    return false;
  }

  return (
    emergencyInfo.emergencyType !== 'Unknown' ||
    emergencyInfo.carCrashTime != null ||
    emergencyInfo.fallDetectionTime != null ||
    emergencyInfo.lossOfPulseTime != null
  );
}

function hasMedicalInfo(elsRecord: ElsRecord): boolean {
  const medicalInfo = elsRecord.medicalInfo;
  if (medicalInfo == null) {
    return false;
  }

  const { lastUpdatedTime, ...textFields } = medicalInfo;
  return lastUpdatedTime != null || Object.values(textFields).some(hasText);
}

function hasEmergencyContact(contact: ElsEmergencyContact): boolean {
  return hasText(contact.name) || hasText(contact.phoneNumber) || hasText(contact.relationship);
}

function TextItem({ title, value }: { title: string; value: string | null }) {
  if (!hasText(value)) {
    return null;
  }

  return (
    <DataPanelInlineItem title={title}>
      <div>{value}</div>
    </DataPanelInlineItem>
  );
}

function DateItem({ title, date, formatType }: { title: string; date: string; formatType: 'combined' | 'timeCombined' }) {
  return (
    <DataPanelInlineItem title={title}>
      <DateFormatter date={date} formatType={formatType} />
    </DataPanelInlineItem>
  );
}

export function ElsDataPanel({ elsRecord }: ElsDataPanelProps) {
  const { t } = useTranslation();

  const { emergencyInfo, medicalInfo, location } = elsRecord;
  const emergencyContacts = elsRecord.emergencyContacts.filter(hasEmergencyContact);

  const emergencyInfoLines: ReactNode[] = [];
  if (emergencyInfo != null) {
    if (emergencyInfo.emergencyType !== 'Unknown') {
      emergencyInfoLines.push(
        <div key="type">
          {t('Incident_InfoPanel_Els_EmergencyType')}: {emergencyInfo.emergencyType}
        </div>,
      );
    }
    if (emergencyInfo.carCrashTime != null) {
      emergencyInfoLines.push(
        <div key="crash">
          {t('Incident_InfoPanel_Els_CarCrashTime')}:{' '}
          <DateFormatter date={emergencyInfo.carCrashTime} formatType="timeCombined" />
        </div>,
      );
    }
    if (emergencyInfo.fallDetectionTime != null) {
      emergencyInfoLines.push(
        <div key="fall">
          {t('Incident_InfoPanel_Els_FallDetectionTime')}:{' '}
          <DateFormatter date={emergencyInfo.fallDetectionTime} formatType="timeCombined" />
        </div>,
      );
    }
    if (emergencyInfo.lossOfPulseTime != null) {
      emergencyInfoLines.push(
        <div key="pulse">
          {t('Incident_InfoPanel_Els_LossOfPulseTime')}:{' '}
          <DateFormatter date={emergencyInfo.lossOfPulseTime} formatType="timeCombined" />
        </div>,
      );
    }
  }

  return (
    <DataPanel header="Google ELS" expandable>
      {hasText(elsRecord.callStartTime) && (
        <DateItem
          title={t('Incident_InfoPanel_Els_CallStartTime')}
          date={elsRecord.callStartTime}
          formatType="combined"
        />
      )}

      {hasText(elsRecord.devicePhoneNumber) && (
        <DataPanelInlineItem
          title={t('Incident_InfoPanel_Els_DevicePhoneNumber')}
          value={elsRecord.devicePhoneNumber}
          allowCopyToClipboard
          linkUrl={`tel:${elsRecord.devicePhoneNumber}`}
        />
      )}

      {hasText(elsRecord.emergencyPhoneNumber) && (
        <DataPanelInlineItem
          title={t('Incident_InfoPanel_Els_EmergencyPhoneNumber')}
          value={elsRecord.emergencyPhoneNumber}
          allowCopyToClipboard
          linkUrl={`tel:${elsRecord.emergencyPhoneNumber}`}
        />
      )}

      {hasText(elsRecord.source) && (
        <DataPanelInlineItem title={t('Incident_InfoPanel_Els_Source')} value={elsRecord.source} />
      )}

      {hasEmergencyInfo(elsRecord) && (
        <DataPanelInlineItem title={t('Incident_InfoPanel_Els_EmergencyInfo')}>
          <div>{emergencyInfoLines}</div>
        </DataPanelInlineItem>
      )}

      {medicalInfo != null && hasMedicalInfo(elsRecord) && (
        <>
          {hasText(medicalInfo.name) && (
            <DataPanelInlineItem title={t('Incident_InfoPanel_Els_Name')}>
              <div>
                <strong>{medicalInfo.name}</strong>
              </div>
            </DataPanelInlineItem>
          )}
          <TextItem title={t('Incident_InfoPanel_Els_DateOfBirth')} value={medicalInfo.dateOfBirth} />
          <TextItem title={t('Incident_InfoPanel_Els_Sex')} value={medicalInfo.sex} />
          <TextItem title={t('Incident_InfoPanel_Els_Gender')} value={medicalInfo.gender} />
          <TextItem title={t('Incident_InfoPanel_Els_Height')} value={medicalInfo.height} />
          <TextItem title={t('Incident_InfoPanel_Els_Weight')} value={medicalInfo.weight} />
          <TextItem title={t('Incident_InfoPanel_Els_BloodType')} value={medicalInfo.bloodType} />
          <TextItem title={t('Incident_InfoPanel_Els_HomeAddress')} value={medicalInfo.homeAddress} />
          <TextItem title={t('Incident_InfoPanel_Els_Allergies')} value={medicalInfo.allergies} />
          <TextItem title={t('Incident_InfoPanel_Els_Medications')} value={medicalInfo.medications} />
          <TextItem title={t('Incident_InfoPanel_Els_Conditions')} value={medicalInfo.conditionsHistory} />
          <TextItem
            title={t('Incident_InfoPanel_Els_DevicesMobilityRequirements')}
            value={medicalInfo.devicesMobilityRequirements}
          />
          <TextItem title={t('Incident_InfoPanel_Els_PregnancyStatus')} value={medicalInfo.pregnancyStatus} />
          <TextItem title={t('Incident_InfoPanel_Els_PregnancyDueDate')} value={medicalInfo.pregnancyDueDate} />
          <TextItem title={t('Incident_InfoPanel_Els_PregnancyInfo')} value={medicalInfo.pregnancyInfo} />
          <TextItem title={t('Incident_InfoPanel_Els_OrganDonor')} value={medicalInfo.organDonor} />
          <TextItem title={t('Incident_InfoPanel_Els_AdvancedDirectives')} value={medicalInfo.advancedDirectives} />
          <TextItem title={t('Incident_InfoPanel_Els_PhysicianInfo')} value={medicalInfo.physicianInfo} />
          <TextItem title={t('Incident_InfoPanel_Els_Other')} value={medicalInfo.other} />
          {medicalInfo.lastUpdatedTime != null && (
            <DateItem
              title={t('Incident_InfoPanel_Els_MedicalDataUpdatedAt')}
              date={medicalInfo.lastUpdatedTime}
              formatType="combined"
            />
          )}
        </>
      )}

      {location != null && (
        <>
          <DataPanelInlineItem title={t('Incident_InfoPanel_Els_Location')}>
            <div>
              {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
            </div>
          </DataPanelInlineItem>

          {location.accuracy != null && (
            <DataPanelInlineItem title={t('Incident_InfoPanel_Els_LocationAccuracy')}>
              <div>{location.accuracy.toFixed(1)} m</div>
            </DataPanelInlineItem>
          )}

          {location.altitude != null && (
            <DataPanelInlineItem title={t('Incident_InfoPanel_Els_LocationAltitude')}>
              <div>{location.altitude.toFixed(1)} m</div>
            </DataPanelInlineItem>
          )}

          {location.verticalAccuracy != null && (
            <DataPanelInlineItem title={t('Incident_InfoPanel_Els_LocationVerticalAccuracy')}>
              <div>{location.verticalAccuracy.toFixed(1)} m</div>
            </DataPanelInlineItem>
          )}

          {location.confidence != null && (
            <DataPanelInlineItem title={t('Incident_InfoPanel_Els_LocationConfidence')}>
              <div>{location.confidence.toFixed(1)}</div>
            </DataPanelInlineItem>
          )}

          {hasText(location.capturedAt) && (
            <DateItem
              title={t('Incident_InfoPanel_Els_LocationCapturedAt')}
              date={location.capturedAt}
              formatType="combined"
            />
          )}

          <TextItem title={t('Incident_InfoPanel_Els_LocationSource')} value={location.source} />
          <TextItem title={t('Incident_InfoPanel_Els_LocationFloor')} value={location.floor} />
        </>
      )}

      {hasText(elsRecord.deviceModel) && (
        <DataPanelInlineItem title={t('Incident_InfoPanel_Els_DeviceModel')}>
          <span>{elsRecord.deviceModel}</span>
        </DataPanelInlineItem>
      )}

      {hasText(elsRecord.deviceLanguages) && (
        <DataPanelInlineItem title={t('Incident_InfoPanel_Els_DeviceLanguages')}>
          <span>{elsRecord.deviceLanguages}</span>
        </DataPanelInlineItem>
      )}

      {hasText(elsRecord.version) && (
        <DataPanelInlineItem title={t('Incident_InfoPanel_Els_Version')}>
          <span>{elsRecord.version}</span>
        </DataPanelInlineItem>
      )}

      {hasText(elsRecord.thunderbirdVersion) && (
        <DataPanelInlineItem title={t('Incident_InfoPanel_Els_ThunderbirdVersion')}>
          <span>{elsRecord.thunderbirdVersion}</span>
        </DataPanelInlineItem>
      )}

      {emergencyContacts.length > 0 && (
        <DataPanelInlineItem title={t('Incident_InfoPanel_Els_EmergencyContacts')}>
          <div className="d-flex flex-column gap-2 w-100">
            {emergencyContacts.map((contact, index) => (
              <div key={`${contact.phoneNumber ?? contact.name ?? ''}-${index}`}>
                {hasText(contact.name) && (
                  <div>
                    <strong>{contact.name}</strong>
                  </div>
                )}
                {hasText(contact.phoneNumber) && (
                  <div>
                    <a href={`tel:${contact.phoneNumber}`}>{contact.phoneNumber}</a>
                  </div>
                )}
                {hasText(contact.relationship) && (
                  <div>
                    {t('Incident_InfoPanel_Els_EmergencyContactRelationship')}: {contact.relationship}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DataPanelInlineItem>
      )}
    </DataPanel>
  );
}
